import React, {
  useCallback, useEffect, useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { IoChevronBack } from 'react-icons/io5';

import {
  InputText, DatePicker, StyledSelect, Button,
} from '../../lib/HooksFormFields';
import HtmlEditor from '../../lib/HooksFormFields/HtmlEditor/HtmlEditor';
import {
  getNewAction, postNewAction, putNewAction,
} from '../../actions/newsActions';
import styles from '../../styles/consult.module.scss';
import editStyles from './EditActu.module.scss';

const statusOptions = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'published', label: 'Publié' },
];

export default function EditActu({ isCreate = false }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { newsItem } = useSelector((state) => state.user);
  const { userAuth } = useSelector((state) => state.auth);
  const [texte, setTexte] = useState('');

  const {
    control, setValue, handleSubmit, watch,
  } = useForm({
    defaultValues: {
      title: '',
      date: new Date(),
      status: 'draft',
      type: '',
    },
  });

  const load = useCallback(() => {
    if (id) getNewAction(dispatch, id);
  }, [dispatch, id]);

  useEffect(() => {
    if (isCreate) {
      setValue('date', new Date());
      setValue('author', userAuth?._id);
    } else if (id) {
      load();
    }
  }, [isCreate, id, load, setValue, userAuth]);

  useEffect(() => {
    if (!newsItem || isCreate) return;
    setValue('title', newsItem.title || '');
    setValue('date', newsItem.date ? new Date(newsItem.date) : new Date());
    setValue('status', newsItem.status || 'draft');
    setValue('type', newsItem.type || '');
    setValue('author', newsItem.author?._id || newsItem.author);
    setTexte(newsItem.texte || '');
  }, [newsItem, isCreate, setValue]);

  async function onSubmit(values, status) {
    const payload = {
      ...values,
      texte,
      status: status || values.status,
      author: values.author || userAuth?._id,
    };
    if (isCreate) {
      const created = await postNewAction(dispatch, payload);
      if (created?._id) navigate(`/majee/actus/${created._id}`);
    } else {
      await putNewAction(dispatch, id, payload);
    }
  }

  return (
    <form className={styles.container} onSubmit={(e) => e.preventDefault()}>
      <Link to="/majee/actus/gestion" className={styles.back}>
        <IoChevronBack />
        Retour à la liste
      </Link>
      <div className={styles.header}>
        <h2>{isCreate ? 'Nouvelle actualité' : (watch('title') || 'Actualité')}</h2>
        <div className={editStyles.actions}>
          <Button
            handleClick={handleSubmit((v) => onSubmit(v, 'draft'))}
            className={editStyles.secondary}
          >
            <span>Brouillon</span>
          </Button>
          <Button
            handleClick={handleSubmit((v) => onSubmit(v, 'published'))}
          >
            <span>Publier</span>
          </Button>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={editStyles.field}>
          <InputText
            name="title"
            control={control}
            label="Titre"
            rules={{ required: 'Titre requis' }}
            placeholder="Titre"
            inline={false}
            widthLabel={0}
          />
        </div>
        <div className={editStyles.row}>
          <div className={editStyles.field}>
            <label htmlFor="date">Date</label>
            <DatePicker name="date" control={control} />
          </div>
          <div className={editStyles.field}>
            <label htmlFor="status">Statut</label>
            <StyledSelect
              name="status"
              control={control}
              options={statusOptions}
              placeholder="Statut"
              onChange={(opt) => setValue('status', opt?.value || 'draft')}
            />
          </div>
          <div className={editStyles.field}>
            <InputText
              name="type"
              control={control}
              label="Type / division"
              rules={{}}
              placeholder="ex. Médical"
              inline={false}
              widthLabel={0}
            />
          </div>
        </div>
        {!isCreate && newsItem?.date && (
          <p className={editStyles.meta}>
            Dernière date :
            {' '}
            {format(new Date(newsItem.date), 'dd/MM/yyyy HH:mm')}
          </p>
        )}
        <div className={editStyles.field}>
          <label htmlFor="texte">Contenu</label>
          <HtmlEditor
            key={isCreate ? 'create' : (newsItem?._id || 'loading')}
            defaultValue={texte}
            onChange={(html) => setTexte(html)}
          />
        </div>
      </div>
    </form>
  );
}
